import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Modal, 
  ModalDialog, 
  ModalClose, 
  Typography, 
  Stack, 
  Button, 
  Alert,
  Box
} from '@mui/joy';
import { 
  useCreateRecipeFromImageMutation
} from '../api';
import { DragDropFileUpload } from '@/components/DragDropFileUpload';
import { compressImageForOCR } from '@/utils/images';

export function AddRecipeFromImagePage() {
  const { householdId } = useParams();
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [createFromImage, { isLoading, error }] = useCreateRecipeFromImageMutation();

  const handleFileSelect = useCallback(async (file) => {
    if (!file || !householdId) return;

    if (!file.type.startsWith('image/')) {
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(file);
    const originalUrl = URL.createObjectURL(file);
    setPreviewUrl(originalUrl);
    
    try {
      const compressedFile = await compressImageForOCR(file);
      const result = await createFromImage({ householdId, file: compressedFile }).unwrap();
      setSelectedFile(null);
      URL.revokeObjectURL(originalUrl);
      setPreviewUrl(null);
      navigate(`/h/${householdId}/recipes/${result.data.id}`);
    } catch (err) {
      console.error('Failed to import recipe from image:', err);
      setSelectedFile(null);
      URL.revokeObjectURL(originalUrl);
      setPreviewUrl(null);
    }
  }, [householdId, createFromImage, navigate, previewUrl]);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    navigate(`/h/${householdId}/recipes`);
  }, [navigate, householdId, previewUrl]);

  return (
    <Modal open={true} onClose={handleClose}>
      <ModalDialog sx={{ maxWidth: '600px', width: '90%' }}>
        <ModalClose />
        <Typography level="h4" sx={{ mb: 1 }}>
          Add Recipe from Image
        </Typography>
        
        <Stack spacing={3}>
          {error && (
            <Alert color="danger">
              {error.data?.error || error.message || 'Failed to import recipe'}
            </Alert>
          )}
          
          <Box>
            <Typography level="body-sm" sx={{ mb: 2 }}>
              Upload a recipe image to automatically extract recipe information
            </Typography>
            
            <DragDropFileUpload
              onFileSelect={handleFileSelect}
              isLoading={isLoading}
              accept="image/*"
              selectedFile={selectedFile}
              previewUrl={previewUrl}
            />
          </Box>
          
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" color="neutral" onClick={handleClose}>Cancel</Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
